import { Box, CircularProgress, debounce, Divider, Pagination } from '@mui/material';
import { useAtom, useAtomValue } from 'jotai';
import { generatePath, useSearchParams } from 'react-router';
import { useTranslation } from 'react-i18next';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Business_Profile_Enum, Contract_History_Bool_Exp, Order_Bool_Exp, Order_Status_Enum } from '@kheops/graphql';
import { RoutePaths } from '../../routes/AppRoutes';
import GuitaristButterflyImage from '../../assets/images/guitarist_butterfly.png';
import useOrders, { DISPUTE_STATUSES, OrdersFilters } from '../../hooks/useOrders';
import EmptyPagePlaceholder from '../../common/components/EmptyPagePlaceholder';
import { getArraysIntersection } from '../../common/utils/common.utils';
import { currentContextAtom, userAtom } from '../../state';
import { orderListUiStateAtom, orderListPageAtom, OrdersCategories, contextToActionNeededStatusMap, orderCatogoryToStatusesMap } from '../state/state';
import SearchInput from '../../common/components/SearchInput';
import OrderTable from './OrderTable';
import OrderFilters from './OrderFilters';
import OrderCategoriesSelection from './OrderCategoriesSelection';

export type OrdersCountByCategoryMap = {
  [status in OrdersCategories]: number;
} & {
  CREATED_BY_ME: number;
  WITH_ACTION_NEEDED: number;
  MY_CLIENTS: number;
  DISPUTE: number;
}

export default function OrderList(): React.JSX.Element {
  const PAGE_SIZE = 20;
  const [searchParams] = useSearchParams();
  const urlOrdersCategory = searchParams.get('category');

  const { t } = useTranslation(['order', 'contracts']);
  const { id: userId } = useAtomValue(userAtom);
  const { realm } = useAtomValue(currentContextAtom);
  const [page, setPage] = useAtom(orderListPageAtom);
  const [offset, setOffset] = useState(0);
  const onlyActionNeededStatuses = contextToActionNeededStatusMap.get(realm)!;
  const [ordersFilters, setOrdersFilters] = useState<OrdersFilters>({
    aggregateInProgressCategoryStatuses: orderCatogoryToStatusesMap.get(OrdersCategories.IN_PROGRESS)!,
    aggregateValidationCategoryStatuses: orderCatogoryToStatusesMap.get(OrdersCategories.VALIDATION)!,
    aggregateDeliveryCategoryStatuses: orderCatogoryToStatusesMap.get(OrdersCategories.DELIVERY)!,
    aggregatePaymentCategoryStatuses: orderCatogoryToStatusesMap.get(OrdersCategories.PAYMENT)!,
    aggregateCompletedCategoryStatuses: orderCatogoryToStatusesMap.get(OrdersCategories.COMPLETED)!,
    searchFilter: {},
  });
  const [orderListUiState, setOrderListUiState] = useAtom(orderListUiStateAtom);
  const { orders, loading, countMap } = useOrders(offset, PAGE_SIZE, ordersFilters, userId, getArraysIntersection([orderCatogoryToStatusesMap.get(orderListUiState.currentSetCategory)!, onlyActionNeededStatuses]));

  const emptyPagePlaceholderProps = realm === Business_Profile_Enum.Buyer
    ? {
      title: t('order:no_orders'),
      description: t('order:no_orders_buyer_description'),
      imageSrc: GuitaristButterflyImage,
      buttonLabel: t('contracts:discover_new_suppliers'),
      linkDestination: generatePath(RoutePaths.DISCOVERY_SUPPLIERS),
    }
    : {
      title: t('order:no_orders'),
      description: t('order:no_orders_supplier_description'),
      imageSrc: GuitaristButterflyImage,
      buttonLabel: t('contracts:discover_new_buyers'),
      linkDestination: generatePath(RoutePaths.DISCOVERY_BUYERS),
    };

  useEffect(() => {
    const { onlyMyOrders, onlyOrdersWhereActionIsNeeded, currentSetCategory, selectedCompanyIds, searchQuery, ordersWithDispute } = orderListUiState;
    let { onlyMyClients } = orderListUiState;

    if (!countMap.MY_CLIENTS) {
      onlyMyClients = false;
    }

    let contractFilter: Contract_History_Bool_Exp | undefined;

    if (selectedCompanyIds.length) {
      contractFilter = realm === Business_Profile_Enum.Buyer
        ? {
          supplyingCompanyId: {
            _in: selectedCompanyIds,
          },
        }
        : {
          buyingCompanyId: {
            _in: selectedCompanyIds,
          },
        };
    }

    const creatorFilter = onlyMyOrders ? { _eq: userId } : {};
    const setCategoriesStatuses = orderCatogoryToStatusesMap.get(currentSetCategory)!;
    const searchFilter: Order_Bool_Exp = searchQuery ? {
      _or: [
        { friendly_id: { _ilike: `%${searchQuery}%` } },
        { contract: { supplying_company: { tradeName: { _ilike: `%${searchQuery}%` } } } },
        { contract: { buying_company: { tradeName: { _ilike: `%${searchQuery}%` } } } },
      ],
    } : {};
    const activeFilterStatusArrays: Order_Status_Enum[][] = [];

    if (onlyOrdersWhereActionIsNeeded) {
      activeFilterStatusArrays.push(onlyActionNeededStatuses);
    } 
    if (ordersWithDispute) {
      activeFilterStatusArrays.push(DISPUTE_STATUSES);
    } 

    if (activeFilterStatusArrays.length) {
      const targetStatuses = getArraysIntersection(activeFilterStatusArrays);
      const getCategoryIntersection = (category: OrdersCategories): Order_Status_Enum[] => getArraysIntersection([orderCatogoryToStatusesMap.get(category)!, targetStatuses]);

      setOrdersFilters({
        contractFilter,
        creatorFilter,
        statusesToGet: getArraysIntersection([setCategoriesStatuses, targetStatuses]),
        aggregateInProgressCategoryStatuses: getCategoryIntersection(OrdersCategories.IN_PROGRESS),
        aggregateValidationCategoryStatuses: getCategoryIntersection(OrdersCategories.VALIDATION),
        aggregateDeliveryCategoryStatuses: getCategoryIntersection(OrdersCategories.DELIVERY),
        aggregatePaymentCategoryStatuses: getCategoryIntersection(OrdersCategories.PAYMENT),
        aggregateCompletedCategoryStatuses: getCategoryIntersection(OrdersCategories.COMPLETED),
        myClientsFilter: onlyMyClients,
        searchFilter,
      });
      return;
    }

    const statusesToGet = setCategoriesStatuses;
    const aggregateInProgressCategoryStatuses = orderCatogoryToStatusesMap.get(OrdersCategories.IN_PROGRESS)!;
    const aggregateValidationCategoryStatuses = orderCatogoryToStatusesMap.get(OrdersCategories.VALIDATION)!;
    const aggregateDeliveryCategoryStatuses = orderCatogoryToStatusesMap.get(OrdersCategories.DELIVERY)!;
    const aggregatePaymentCategoryStatuses = orderCatogoryToStatusesMap.get(OrdersCategories.PAYMENT)!;
    const aggregateCompletedCategoryStatuses = orderCatogoryToStatusesMap.get(OrdersCategories.COMPLETED)!;

    setOrdersFilters({
      contractFilter,
      creatorFilter,
      statusesToGet,
      aggregateInProgressCategoryStatuses,
      aggregateValidationCategoryStatuses,
      aggregateDeliveryCategoryStatuses,
      aggregatePaymentCategoryStatuses,
      aggregateCompletedCategoryStatuses,
      myClientsFilter: onlyMyClients,
      searchFilter,
    });
  }, [orderListUiState, countMap]);

  const handleCategoryChange = useCallback((selectedCategory: OrdersCategories): void => {
    const newUiState = {
      ...orderListUiState,
      currentSetCategory: selectedCategory,
    };

    setOrderListUiState(newUiState);
    setPage(1);
    setOffset(0);
    window.history.replaceState({
      ...window.history.state,
      uiState: {
        ...newUiState,
        page: 1,
      },
    }, '');
  }, [page, orderListUiState]);

  const handlePageChange = useCallback((newPage: number): void => {
    setPage(newPage);
    setOffset(PAGE_SIZE * (newPage - 1));
    window.scrollTo(0, 0);
    window.history.replaceState({
      ...window.history.state,
      uiState: {
        ...orderListUiState,
        page: newPage,
      },
    }, '');
  }, [orderListUiState]);

  useEffect(() => {
    if (window.history.state.uiState) {
      setOrderListUiState({
        ...orderListUiState,
        ...window.history.state.uiState,
      });
      setPage(window.history.state.uiState.page);
      setOffset(PAGE_SIZE * (window.history.state.uiState.page - 1));
    }
  }, []);

  const thereAreNoOrdersToFetch = useMemo(() => {
    return Object.keys(countMap).every((key) => countMap[key as keyof OrdersCountByCategoryMap] === 0);
  }, [orderListUiState, countMap]);

  const mainComponent = useMemo(() => {
    if (thereAreNoOrdersToFetch) {
      return <EmptyPagePlaceholder {...emptyPagePlaceholderProps} sx={{ pt: 4 }} />;
    }

    return (
      <>
        <OrderTable orders={orders} />
        {!!orders.length && (
          <Pagination
            color="primary"
            sx={{
              '& .MuiPagination-ul': {
                justifyContent: 'center',
              },
            }}
            page={page}
            count={Math.ceil(countMap[orderListUiState.currentSetCategory] / PAGE_SIZE)}
            onChange={(event, newPage) => handlePageChange(newPage)}
          />
        )}
      </>
    );
  }, [orders, page, countMap, thereAreNoOrdersToFetch]);
  const [displayedTextSearch, setDisplayedTextSearch] = useState(window.history.state.uiState?.searchQuery);

  const debouncedTextSearch = useCallback(
    debounce((query: string) => {
      const newUiState = {
        ...orderListUiState,
        searchQuery: query,
      };

      setOrderListUiState(newUiState);

      window.history.replaceState({
        ...window.history.state,
        uiState: {
          ...newUiState,
          page: 1,
        },
      }, '');
    }, 400),
    [orderListUiState, setOrderListUiState],
  );

  const handleTextSearch = (query: string): void => {
    setDisplayedTextSearch(query);
    debouncedTextSearch(query);
  };

  useEffect(() => {
    setDisplayedTextSearch(orderListUiState.searchQuery);
  }, [orderListUiState.searchQuery]);

  useEffect(() => {
    if(urlOrdersCategory === 'validation') {
      handleCategoryChange(OrdersCategories.VALIDATION);
    }
  }, [urlOrdersCategory]);

  return (
    // eslint-disable-next-line react/jsx-no-useless-fragment
    <>
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
        }}
      >
        <OrderCategoriesSelection
          ordersCountByCategory={countMap}
          handleCategoryChange={handleCategoryChange}
        />
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            alignItems: {
              xs: 'flex-start',
              lg: 'center',
            },
            flexDirection: {
              xs: 'column',
              lg: 'row',
            },
          }}
        >
          <SearchInput
            sx={{
              minWidth: {
                xs: '-webkit-fill-available',
                lg: 396,
              },
            }}
            placeholder={t('order:search_an_order')}
            value={displayedTextSearch}
            onChange={handleTextSearch}
          />
          <Divider
            flexItem
            orientation="vertical"
            sx={{
              display: {
                xs: 'none',
                lg: 'flex',
              },
              my: 1,
            }}
          />
          <OrderFilters ordersCountByCategory={countMap} />
        </Box>
        { loading
          ? (
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <CircularProgress />
            </Box>
          )
          : (mainComponent)}
      </Box>
    </>
  );
}
